import React, { useEffect, useRef, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { POINTS_UPDATED_EVENT } from '../../events/sessionEvents';
import { api } from '../../services/api';
import { MeResponse } from '../../types';
import AnimatedPoints from './AnimatedPoints';

// Retain the last response across page mounts while each navbar refreshes it.
let cachedSession: MeResponse | null = null;

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const isDashboard = useMatch('/dashboard') !== null;
  const [session, setSession] = useState<MeResponse | null>(() => cachedSession);
  const [adminOpen, setAdminOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const adminRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const sessionRequestRef = useRef(0);

  useEffect(() => {
    const loadSession = () => {
      const requestId = ++sessionRequestRef.current;
      api.me()
        .then(me => {
          if (requestId === sessionRequestRef.current) {
            cachedSession = me;
            setSession(me);
          }
        })
        .catch(() => {
          if (requestId === sessionRequestRef.current) {
            setSession(previous => previous ?? { authenticated: false });
          }
        });
    };

    loadSession();
    window.addEventListener(POINTS_UPDATED_EVENT, loadSession);
    return () => {
      window.removeEventListener(POINTS_UPDATED_EVENT, loadSession);
      sessionRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMenus = () => {
    setMenuOpen(false);
    setAdminOpen(false);
  };

  const handleSignOut = async () => {
    try { await api.logout(); } catch {}
    // Ignore any refresh started before sign-out completed.
    sessionRequestRef.current += 1;
    cachedSession = { authenticated: false };
    setSession(cachedSession);
    closeMenus();
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="navbar-inner" ref={navRef}>
        <Link className="navbar-brand" to={session?.authenticated && !isDashboard ? '/dashboard' : '/'} onClick={closeMenus}>
          <img src="/logo.webp" alt="TeachMeNow Logo" className="navbar-brand-logo" />
          TeachMeNow
        </Link>

        {session !== null && (
          <button
            type="button"
            className={`navbar-menu-toggle${menuOpen ? ' is-open' : ''}`}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
            aria-controls="navbar-menu"
            onClick={() => setMenuOpen(open => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        )}

        {session?.authenticated ? (
          <div id="navbar-menu" className={`navbar-menu${menuOpen ? ' is-open' : ''}`}>
            <div className="navbar-links">
              <Link to="/dashboard" className="navbar-link" onClick={closeMenus}>Dashboard</Link>
              <Link to="/browse" className="navbar-link" onClick={closeMenus}>All Courses</Link>
              <Link to="/flashcards" className="navbar-link" onClick={closeMenus}>Flashcards</Link>
              {session.user?.is_admin ? (
                <div className="navbar-dropdown" ref={adminRef}>
                  <button
                    type="button"
                    className={`navbar-link navbar-dropdown-trigger${adminOpen ? ' is-open' : ''}`}
                    onClick={() => setAdminOpen(o => !o)}
                  >
                    Admin
                    <svg className="navbar-dropdown-chevron" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 4l4 4 4-4" />
                    </svg>
                  </button>
                  {adminOpen && (
                    <div className="navbar-dropdown-menu">
                      <Link to="/admin/courses" className="navbar-dropdown-item" onClick={closeMenus}>Manage Courses</Link>
                      <Link to="/admin/users" className="navbar-dropdown-item" onClick={closeMenus}>Manage Users</Link>
                      <Link to="/admin/organisations" className="navbar-dropdown-item" onClick={closeMenus}>Manage Organisations</Link>
                      <Link to="/admin/send-email" className="navbar-dropdown-item" onClick={closeMenus}>Send Email</Link>
                      <Link to="/admin/prompt-tester" className="navbar-dropdown-item" onClick={closeMenus}>Prompt Tester</Link>
                      <Link to="/preview/markdown" className="navbar-dropdown-item" onClick={closeMenus}>Markdown Preview</Link>
                    </div>
                  )}
                </div>
              ) : (
                session.user.organisation_id
                && (session.user.organisation_role === 'ADMIN' || session.user.organisation_role === 'TEACHER')
                && (
                  <Link
                    to={`/manage/${session.user.organisation_id}`}
                    className="navbar-link"
                    onClick={closeMenus}
                  >
                    Manage
                  </Link>
                )
              )}
            </div>
            <div className="navbar-actions">
              <div className="navbar-gamification" aria-label="Your learning progress">
                <AnimatedPoints points={session.user.points} />
                <span
                  className={`navbar-stat navbar-stat--streak${session.user.streak_active ? ' is-active' : ''}`}
                  aria-label={`${session.user.current_streak} day streak`}
                  title={`Current streak: ${session.user.current_streak} days · Best: ${session.user.max_streak} days`}
                >
                  {session.user.streak_active && (
                    <span className="navbar-stat-icon" aria-hidden="true">🔥</span>
                  )}
                  <span className="navbar-stat-value">{session.user.current_streak}</span>
                  <span className="navbar-stat-label">day streak</span>
                </span>
              </div>
              <button type="button" className="navbar-link" onClick={handleSignOut}>Sign Out</button>
            </div>
          </div>
        ) : session !== null ? (
          <div id="navbar-menu" className={`navbar-menu${menuOpen ? ' is-open' : ''}`}>
            <div className="navbar-actions">
              <Link to="/login" className="navbar-link" style={{ fontWeight: 500 }} onClick={closeMenus}>Sign In</Link>
              <Link to="/register" className="navbar-cta" onClick={closeMenus}>Register</Link>
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );
};

export default Navbar;
